import { useCallback, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState } from 'recoil';
import { useSelectedPrimaryImage } from '~/hooks/image/useSelectedImages';
import { useRegistrationListView } from '~/hooks/domain/useRegistrationListView';
import useProcessDicomFiles from '~/hooks/useProcessDicomFiles';
import { contourPropagationTaskState } from '~/state/atoms/adapt/contourPropagationTaskAtom';
import { enqueueSnackbar } from '~/utils/notifications';
import type { DatasetOption } from './DatasetSelect';
import { usePreselectFirst } from './usePreselectFirst';

export function acceptsInitRigid(modelName?: string): boolean {
    const parts = (modelName ?? '').split('_');
    return parts.length >= 4 && parts[0] === 'reg' && parts[2] === 'cc';
}

export interface InitRigidChoices {
    readonly offered: boolean;
    readonly enabled: boolean;
    readonly setEnabled: (enabled: boolean) => void;
    readonly options: readonly DatasetOption[];
    readonly selectedUid?: string;
    readonly select: (sopInstanceUid: string) => void;
    readonly importFromFile: () => void;
    readonly fileInputProps: {
        ref: React.RefObject<HTMLInputElement>;
        onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    };
}

export function useInitRigidChoices(
    targetSeriesUid: string | undefined
): InitRigidChoices {
    const intl = useIntl();
    const [task, setTask] = useRecoilState(contourPropagationTaskState);
    const primaryImage = useSelectedPrimaryImage();
    const groups = useRegistrationListView(primaryImage ?? null);
    const { processImportedFiles } = useProcessDicomFiles();
    const fileInput = useRef<HTMLInputElement>(null);

    const options = (
        groups.find((group) => group.imageSeriesUid === targetSeriesUid)
            ?.registrations ?? []
    )
        .filter((registration) => registration.registrationKind === 'rigid')
        .map((registration) => ({
            uid: registration.sopInstanceUid,
            name:
                registration.contentLabel ||
                intl.formatMessage({ id: 'registration' }),
        }));

    const setEnabled = (enabled: boolean) =>
        setTask((current) => ({
            ...current,
            initRigid: {
                enabled,
                sopInstanceUid: enabled
                    ? current.initRigid?.sopInstanceUid
                    : undefined,
            },
        }));

    const select = useCallback(
        (sopInstanceUid: string) =>
            setTask((current) => ({
                ...current,
                initRigid: { enabled: true, sopInstanceUid },
            })),
        [setTask]
    );

    const enabled = task.initRigid?.enabled === true;
    const selectedUid = task.initRigid?.sopInstanceUid;
    usePreselectFirst({
        options: enabled ? options : [],
        hasSelection: options.some((option) => option.uid === selectedUid),
        onSelect: (option) => select(option.uid),
    });

    const onFilesChosen = async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const files = Array.from(event.target.files ?? []);
        event.target.value = '';
        if (files.length === 0) {
            return;
        }
        const result = await processImportedFiles(files);
        const usable = result?.registrations?.[0];
        if (!usable) {
            enqueueSnackbar(
                intl.formatMessage({ id: 'replanning.initRigid.notUsable' }),
                { variant: 'warning' }
            );
            return;
        }
        select(usable.sopInstanceUid);
    };

    return {
        offered: acceptsInitRigid(task.propagationModelName),
        enabled,
        setEnabled,
        options,
        selectedUid,
        select,
        importFromFile: () => fileInput.current?.click(),
        fileInputProps: {
            ref: fileInput,
            onChange: (event) => void onFilesChosen(event),
        },
    };
}
